import type { AdvisorPoint, AdvisorRegion } from './types';
import { haversineKm } from './v3-results';

// Chapters are the country's regions (XT custom regions). A product or a
// chapter's hotel belongs to the region whose outline holds its map point —
// the same test the catalogue's region filter makes (ST_Contains).

const hasOutline = (region: AdvisorRegion): boolean =>
  (region.boundary?.length ?? 0) >= 3;

/** Ray casting on lng/lat; the outline may be closed or open. */
export const pointInOutline = (
  point: AdvisorPoint,
  outline: readonly AdvisorPoint[]
): boolean => {
  let inside = false;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const a = outline[i];
    const b = outline[j];
    if (!a || !b) continue;
    if (
      a.lat > point.lat !== b.lat > point.lat &&
      point.lng <
        ((b.lng - a.lng) * (point.lat - a.lat)) / (b.lat - a.lat) + a.lng
    ) {
      inside = !inside;
    }
  }
  return inside;
};

const outlineArea = (outline: readonly AdvisorPoint[]): number => {
  let sum = 0;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const a = outline[i];
    const b = outline[j];
    if (a && b) sum += (b.lng + a.lng) * (b.lat - a.lat);
  }
  return Math.abs(sum / 2);
};

const outlineCentre = (outline: readonly AdvisorPoint[]): AdvisorPoint => ({
  lat: outline.reduce((sum, point) => sum + point.lat, 0) / outline.length,
  lng: outline.reduce((sum, point) => sum + point.lng, 0) / outline.length,
});

const cityKey = (city: string): string =>
  city.normalize('NFD').replace(/\p{M}/gu, '').trim().toLowerCase();

/**
 * The one top-level region whose XT city list (or a sub-region's) names the
 * city. None when no region, or more than one, lists it.
 */
export const regionForCity = (
  city: string | null | undefined,
  regions: readonly AdvisorRegion[]
): AdvisorRegion | undefined => {
  const key = cityKey(city ?? '');
  if (!key) return undefined;
  const found = new Map<string, AdvisorRegion>();
  for (const region of regions) {
    if (!(region.cities ?? []).some((entry) => cityKey(entry) === key)) {
      continue;
    }
    const top = chapterRegionOf(region, regions);
    if (top.parentId === null) found.set(top.id, top);
  }
  return found.size === 1 ? [...found.values()][0] : undefined;
};

/** The top-level region a region belongs to (itself when top-level). */
export const chapterRegionOf = (
  region: AdvisorRegion,
  regions: readonly AdvisorRegion[]
): AdvisorRegion =>
  (region.parentId && regions.find((entry) => entry.id === region.parentId)) ||
  region;

/**
 * The chapter region for a map point: the smallest top-level region whose
 * outline holds it (`prefer` wins when it is one of them), else the nearest
 * one (unless `nearest` is off). None when no top-level region has an
 * outline.
 */
export const regionAtPoint = (
  point: AdvisorPoint,
  regions: readonly AdvisorRegion[],
  prefer: string | null = null,
  nearest = true
): AdvisorRegion | undefined => {
  const outlined = regions.filter(
    (region) => region.parentId === null && hasOutline(region)
  );
  const holding = outlined.filter((region) =>
    pointInOutline(point, region.boundary ?? [])
  );
  if (holding.length > 0) {
    return (
      holding.find((region) => region.id === prefer) ??
      [...holding].sort(
        (a, b) => outlineArea(a.boundary ?? []) - outlineArea(b.boundary ?? [])
      )[0]
    );
  }
  if (!nearest) return undefined;
  let closest: AdvisorRegion | undefined;
  let best = Number.POSITIVE_INFINITY;
  for (const region of outlined) {
    const km = haversineKm(point, outlineCentre(region.boundary ?? []));
    if (km < best) {
      best = km;
      closest = region;
    }
  }
  return closest;
};
